import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Package, Users, TrendingUp, AlertTriangle, Clock, Zap, Award, Sparkles, MessageCircle,
  CheckCircle2, Loader2, ArrowRight, Hammer,
} from "lucide-react";
import { format } from "date-fns";
import { WhatsAppWishDialog } from "@/components/WhatsAppWishDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";

const GREEN = "#0F5E39";
const GOLD = "#C9A24B";
const GOLD_SOFT = "#E4D3A0";
const MUTED = "#4A5A50";

function KpiCard({ label, value, icon: Icon, tone, onClick, testId, hint }) {
  const toneCls = {
    green: "bg-[#E1F0E6] text-[#0F5E39]",
    gold: "bg-[#F6EED8] text-[#6E5416]",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
    slate: "bg-[#F1EDDF] text-[#4A5A50]",
  }[tone || "green"];

  return (
    <button
      onClick={onClick}
      className="text-left w-full bg-white rounded-2xl border border-[#E4DFCF] p-5 hover:shadow-[0_12px_32px_rgba(20,38,28,0.08)] hover:-translate-y-[2px] transition group"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] font-semibold">{label}</div>
          <div className="font-serif text-4xl font-medium mt-2 text-[#C9A24B]">{value}</div>
          {hint && <div className="text-xs text-[#4A5A50] mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${toneCls}`}>
          <Icon size={18} strokeWidth={1.6}/>
        </div>
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#0F5E39] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        View list <ArrowRight size={12}/>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const { user, company } = useAuth();
  const nav = useNavigate();
  const [orderStats, setOrderStats] = useState(null);
  const [crmStats, setCrmStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [salesRange, setSalesRange] = useState("30d");
  const [ordersRange, setOrdersRange] = useState("30d");
  const [salesTrend, setSalesTrend] = useState([]);
  const [ordersTrend, setOrdersTrend] = useState([]);
  const [wishOpen, setWishOpen] = useState(false);
  const [wishContact, setWishContact] = useState(null);
  const [wishOccasion, setWishOccasion] = useState(null);

  const openWish = async (occ) => {
    try {
      const { data } = await api.get(`/contacts/${occ.contact_id}`);
      setWishContact(data);
      setWishOccasion(occ);
      setWishOpen(true);
    } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: o }, { data: c }, { data: recent }] = await Promise.all([
          api.get("/dashboard/orders"),
          api.get("/dashboard/crm"),
          api.get("/orders", { params: { page: 1, page_size: 5 } }),
        ]);
        setOrderStats(o); setCrmStats(c); setRecentOrders(recent.items || []);
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/trend?range=${salesRange}`);
        setSalesTrend(data.points || []);
      } catch (_) { setSalesTrend([]); }
    })();
  }, [salesRange]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/trend?range=${ordersRange}`);
        setOrdersTrend(data.points || []);
      } catch (_) { setOrdersTrend([]); }
    })();
  }, [ordersRange]);

  const pending = (orderStats?.by_status?.new || 0) + (orderStats?.by_status?.accepted || 0);
  const wip = orderStats?.by_status?.["in-progress"] || 0;
  const completed = orderStats?.by_status?.completed || 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Overview</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-1 text-[#0F1E15]">
            Good day, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#4A5A50] mt-1 italic font-serif">"We shape gold" — here's how your atelier is running today.</p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5A50]">{company?.name}</div>
      </div>

      {["admin","super-admin","manager","accounts"].includes(user?.role) && <OpsPulseTile/>}
      {["admin","super-admin","manager","accounts"].includes(user?.role) && <PriceAlertsKpi/>}

      {/* KPI CARDS — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Total Orders"
          value={orderStats?.total ?? "—"}
          icon={Package}
          tone="green"
          onClick={() => nav("/orders")}
          testId="kpi-total-orders"
          hint="All orders in workspace"
        />
        <KpiCard
          label="Pending"
          value={pending}
          icon={Clock}
          tone="amber"
          onClick={() => nav("/orders?status=new")}
          testId="kpi-pending-orders"
          hint="New + accepted, not started"
        />
        <KpiCard
          label="WIP"
          value={wip}
          icon={Loader2}
          tone="blue"
          onClick={() => nav("/orders?status=in-progress")}
          testId="kpi-wip-orders"
          hint="In production"
        />
        <KpiCard
          label="Completed"
          value={completed}
          icon={CheckCircle2}
          tone="gold"
          onClick={() => nav("/orders?status=completed")}
          testId="kpi-completed-orders"
          hint="Finished orders"
        />
      </div>

      {/* Order Overview — charts with range toggle */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-6 bg-[#C9A24B] rounded-full"/>
          <h2 className="font-serif text-2xl font-medium">Order Overview</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TrendCard
            title="Sales (grams)"
            subtitle="Aggregate weight processed"
            range={salesRange}
            onRangeChange={setSalesRange}
            data={salesTrend}
            testId="sales-chart"
            variant="area"
            valueKey="sales"
            colorMain={GREEN}
            colorFill="rgba(12,110,61,0.15)"
          />
          <TrendCard
            title="Orders"
            subtitle="Order count"
            range={ordersRange}
            onRangeChange={setOrdersRange}
            data={ordersTrend}
            testId="orders-chart"
            variant="bar"
            valueKey="orders"
            colorMain={GOLD}
          />
        </div>
      </section>

      {/* Tabbed operational view */}
      <section>
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="bg-[#F1EDDF]">
            <TabsTrigger value="orders" data-testid="dash-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="dash-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="production" data-testid="dash-tab-production">Production</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2 border-[#E4DFCF] rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-serif text-lg font-medium">Recent orders</div>
                    <button onClick={() => nav("/orders")} className="text-sm text-[#0F5E39] hover:underline inline-flex items-center gap-1" data-testid="see-all-orders">
                      See all <ArrowRight size={12}/>
                    </button>
                  </div>
                  <div className="divide-y divide-[#E4DFCF]">
                    {recentOrders.length ? recentOrders.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => nav(`/orders/${o.id}`)}
                        className="w-full flex items-center justify-between py-3 hover:bg-[#F1EDDF] px-2 rounded-lg text-left"
                        data-testid="recent-order-row"
                      >
                        <div>
                          <div className="font-medium">{o.reference_number}</div>
                          <div className="text-xs text-[#4A5A50]">{o.client_name || "—"} · {o.karigar_name || "unassigned"}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusPill status={o.priority}/>
                          <StatusPill status={o.status}/>
                        </div>
                      </button>
                    )) : <div className="text-sm text-[#4A5A50] py-4">No orders yet</div>}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#E4DFCF] rounded-xl">
                <CardContent className="p-6">
                  <div className="font-serif text-lg font-medium mb-3">Status breakdown</div>
                  <div className="space-y-2">
                    {orderStats && Object.entries(orderStats.by_status || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <StatusPill status={k}/>
                        <span className="text-sm font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-2">By priority</div>
                    <div className="space-y-2">
                      {orderStats && Object.entries(orderStats.by_priority || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between">
                          <StatusPill status={k}/>
                          <span className="text-sm font-medium">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard label="Total contacts" value={crmStats?.total_contacts ?? "—"} icon={Users} tone="green" onClick={()=>nav("/contacts")} testId="stat-contacts"/>
              <KpiCard label="New leads (month)" value={crmStats?.new_leads_this_month ?? "—"} icon={TrendingUp} tone="blue" onClick={()=>nav("/contacts?contact_type=lead")} testId="stat-leads"/>
              <KpiCard label="Win rate" value={`${crmStats?.win_rate ?? 0}%`} icon={Award} tone="gold" onClick={()=>nav("/deals")} testId="stat-winrate"/>
              <KpiCard label="Pipeline" value={"₹" + (Object.values(crmStats?.pipeline_by_stage || {}).reduce((a,b)=>a+b,0)).toLocaleString('en-IN')} icon={TrendingUp} tone="gold" onClick={()=>nav("/deals")} testId="stat-pipeline"/>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-[#E4DFCF] rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-[#C9A24B]"/><div className="font-serif text-lg font-medium">Upcoming Occasions</div></div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {crmStats?.upcoming_occasions?.length ? crmStats.upcoming_occasions.map((o) => (
                      <div key={`${o.contact_id}-${o.date}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#E4DFCF] hover:bg-[#F1EDDF]" data-testid="occasion-item">
                        <button onClick={() => nav(`/contacts/${o.contact_id}`)} className="text-left flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{o.contact_name}</div>
                          <div className="text-xs text-[#4A5A50]">{o.label} · {o.date}</div>
                        </button>
                        <Button size="sm" variant="outline" onClick={() => openWish(o)} className="rounded-full h-8 px-3 text-xs border-[#25D366] text-[#1B8B4B] hover:bg-[#25D366]/10" data-testid={`send-wish-${o.contact_id}`}>
                          <MessageCircle size={12} className="mr-1"/> Wish
                        </Button>
                      </div>
                    )) : <div className="text-sm text-[#4A5A50]">No occasions in the next 30 days</div>}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#E4DFCF] rounded-xl">
                <CardContent className="p-6">
                  <div className="font-serif text-lg font-medium mb-3">Top customers</div>
                  {crmStats?.top_customers?.length ? (
                    <div className="space-y-2">
                      {crmStats.top_customers.map((c, i) => (
                        <button key={c.id} onClick={() => nav(`/contacts/${c.id}`)} className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#F1EDDF] border border-transparent hover:border-[#E4DFCF]">
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-[#0F5E39] text-white text-xs flex items-center justify-center font-medium">{i + 1}</div>
                            <div>
                              <div className="font-medium text-sm">{c.name}</div>
                              <div className="text-[10px] uppercase text-[#4A5A50] tracking-widest">Lifetime</div>
                            </div>
                          </div>
                          <div className="font-serif text-[#C9A24B]">{c.value.toLocaleString('en-IN')} g</div>
                        </button>
                      ))}
                    </div>
                  ) : <div className="text-sm text-[#4A5A50]">No customer data yet</div>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="production" className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard label="Urgent" value={orderStats?.urgent ?? "—"} icon={Zap} tone="red" onClick={()=>nav("/orders?priority=urgent")} testId="stat-urgent"/>
              <KpiCard label="Due this week" value={orderStats?.due_this_week ?? "—"} icon={Clock} tone="amber" onClick={()=>nav("/orders")} testId="stat-due-week"/>
              <KpiCard label="Overdue" value={orderStats?.overdue ?? "—"} icon={AlertTriangle} tone="red" onClick={()=>nav("/orders")} testId="stat-overdue"/>
              <KpiCard label="On hold" value={orderStats?.by_status?.["on-hold"] ?? 0} icon={AlertTriangle} tone="slate" onClick={()=>nav("/orders?status=on-hold")} testId="stat-onhold"/>
            </div>

            <Card className="border-[#E4DFCF] rounded-xl mt-4">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Hammer size={16} className="text-[#C9A24B]"/>
                  <div className="font-serif text-lg font-medium">Orders per karigar</div>
                </div>
                {orderStats && Object.keys(orderStats.by_karigar || {}).length ? (
                  <div className="space-y-2">
                    {Object.entries(orderStats.by_karigar).map(([k, v]) => {
                      const max = Math.max(...Object.values(orderStats.by_karigar));
                      const pct = (v / max) * 100;
                      return (
                        <div key={k}>
                          <div className="flex items-center justify-between text-sm">
                            <span>{k}</span>
                            <span className="font-medium text-[#0F5E39]">{v}</span>
                          </div>
                          <div className="mt-1 h-2 bg-[#F1EDDF] rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#0F5E39] to-[#C9A24B]" style={{ width: `${pct}%` }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div className="text-sm text-[#4A5A50]">No karigars assigned</div>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </section>

      <WhatsAppWishDialog
        open={wishOpen}
        onOpenChange={setWishOpen}
        contact={wishContact}
        occasion={wishOccasion}
        companyName={company?.name}
      />
    </div>
  );
}

function TrendCard({ title, subtitle, range, onRangeChange, data, testId, variant, valueKey, colorMain, colorFill }) {
  const empty = !data || data.length === 0 || data.every(p => (p[valueKey] || 0) === 0);
  return (
    <Card className="border-[#E4DFCF] rounded-xl">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="font-serif text-lg font-medium">{title}</div>
            {subtitle && <div className="text-xs text-[#4A5A50]">{subtitle}</div>}
          </div>
          <div className="inline-flex bg-[#F1EDDF] rounded-full p-1 text-xs">
            <button onClick={() => onRangeChange("30d")} className={`px-3 py-1 rounded-full ${range === "30d" ? "bg-white text-[#0F5E39] shadow-sm font-medium" : "text-[#4A5A50]"}`} data-testid={`${testId}-range-30d`}>Last 30 days</button>
            <button onClick={() => onRangeChange("12m")} className={`px-3 py-1 rounded-full ${range === "12m" ? "bg-white text-[#0F5E39] shadow-sm font-medium" : "text-[#4A5A50]"}`} data-testid={`${testId}-range-12m`}>Last 12 months</button>
          </div>
        </div>
        <div className="h-56 mt-3" data-testid={testId}>
          {empty ? (
            <div className="h-full flex items-center justify-center text-sm text-[#4A5A50]">No data in this range</div>
          ) : variant === "area" ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#E4DFCF" strokeDasharray="4 4" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E4DFCF", fontSize: 12 }}/>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colorMain} stopOpacity={0.35}/>
                    <stop offset="95%" stopColor={colorMain} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey={valueKey} stroke={colorMain} strokeWidth={2} fill="url(#salesGrad)"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#E4DFCF" strokeDasharray="4 4" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E4DFCF", fontSize: 12 }}/>
                <Bar dataKey={valueKey} fill={colorMain} radius={[6, 6, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function PriceAlertsKpi() {
  const [count, setCount] = React.useState(null);
  const [threshold, setThreshold] = React.useState(5);
  const [topAlert, setTopAlert] = React.useState(null);
  const nav = useNavigate();
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/procurement/alerts?open_only=true&limit=20");
        if (cancelled) return;
        const items = data?.items || [];
        setCount(items.length);
        setThreshold(Number(data?.threshold_pct ?? 5));
        setTopAlert(items[0] || null);
      } catch (_) { /* module may be disabled */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (count === null || count === 0) return null;
  return (
    <Card
      className="border-[#E4DFCF] rounded-2xl bg-gradient-to-r from-red-50/70 to-white cursor-pointer hover:shadow-md transition"
      onClick={() => nav("/procurement")}
      data-testid="price-alerts-kpi"
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="h-10 w-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
          <span className="font-serif text-lg" data-testid="price-alerts-count">{count}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Open vendor price alerts</div>
          <div className="font-serif text-lg text-red-800">
            {count === 1 ? "1 vendor rate exceeded your threshold" : `${count} vendor rates exceeded your ${threshold}% threshold`}
          </div>
          {topAlert && (
            <div className="text-xs text-[#4A5A50] mt-1 truncate">
              Top: {topAlert.consumable_name || topAlert.consumable_id} · {topAlert.vendor_name || "vendor"} ↑ {Number(topAlert.increase_percent).toFixed(2)}%
            </div>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-red-700 shrink-0">Review →</span>
      </CardContent>
    </Card>
  );
}


function OpsPulseTile() {
  const [cash, setCash] = React.useState(null);
  const [audit, setAudit] = React.useState(null);
  const [spark, setSpark] = React.useState(null);
  const nav = useNavigate();
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, a, s] = await Promise.all([
          api.get("/cash-flow/forecast?days=30").catch(() => null),
          api.get("/audit-hub/?since_hours=24&limit=1").catch(() => null),
          api.get("/audit-hub/pulse/sparklines?days=30").catch(() => null),
        ]);
        if (cancelled) return;
        setCash(c?.data || null);
        setAudit(a?.data || null);
        setSpark(s?.data || null);
      } catch (_) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!cash && !audit) return null;
  const cashTotal = cash?.total_outflow || 0;
  const eventCount = audit?.count || 0;
  const byKind = audit?.counts_by_kind || {};
  return (
    <Card className="border-[#E4DFCF] rounded-2xl bg-gradient-to-r from-[#F6F1E1]/60 to-white" data-testid="ops-pulse-tile">
      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="cursor-pointer" onClick={() => nav("/cash-flow")}>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Next 30 days · outflow</div>
          <div className="font-serif text-3xl text-[#083F22]" data-testid="ops-pulse-cash">₹{cashTotal.toLocaleString("en-IN")}</div>
          <div className="text-xs text-[#4A5A50] mt-1">
            EMI ₹{(cash?.by_kind?.asset_emi || 0).toLocaleString("en-IN")} · PO ₹{(cash?.by_kind?.po_due || 0).toLocaleString("en-IN")} · Payroll ₹{(cash?.by_kind?.payroll || 0).toLocaleString("en-IN")}
          </div>
          {spark?.cash_series?.length > 0 && <Sparkline data={spark.cash_series.map((r) => r.amount)} color="#083F22" testid="sparkline-cash"/>}
        </div>
        <div className="cursor-pointer md:border-l md:border-[#E4DFCF] md:pl-4" onClick={() => nav("/audit-hub")}>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Last 24h · activity</div>
          <div className="font-serif text-3xl text-[#0F5E39]" data-testid="ops-pulse-events">{eventCount} events</div>
          <div className="text-xs text-[#4A5A50] mt-1 flex flex-wrap gap-2">
            {Object.entries(byKind).slice(0, 5).map(([k, v]) => (
              <span key={k} className="px-2 py-0.5 rounded-full bg-white border border-[#E4DFCF] text-[10px]">{k} · {v}</span>
            ))}
          </div>
          {spark?.audit_series?.length > 0 && <Sparkline data={spark.audit_series.map((r) => r.count)} color="#0F5E39" testid="sparkline-audit"/>}
        </div>
        <div className="md:border-l md:border-[#E4DFCF] md:pl-4 flex items-center justify-end">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Get the roll-up</div>
            <button
              onClick={async () => {
                try {
                  const backend = process.env.REACT_APP_BACKEND_URL || "";
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${backend}/api/reports/weekly-digest.pdf`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    credentials: "include",
                  });
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (_) { /* noop */ }
              }}
              className="mt-1 rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15] px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="ops-pulse-digest-btn"
            >
              Weekly digest PDF
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



/** Tiny inline SVG sparkline — no chart library needed. */
function Sparkline({ data, color = "#0F5E39", height = 32, testid }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const w = 100; // viewBox width
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(2)},${((1 - (v - min) / range) * height).toFixed(2)}`);
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none"
      className="w-full mt-2" style={{ height }}
      data-testid={testid}
    >
      <polyline
        fill="none" stroke={color} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"
        points={pts.join(" ")}
      />
      <polyline
        fill={`${color}22`} stroke="none"
        points={`0,${height} ${pts.join(" ")} ${w},${height}`}
      />
    </svg>
  );
}

